/**
 * Reseller → Commission plans (TMMPDM-0041, TMMAPI-0104, MM-D-106): the reseller's own affiliate
 * commission plans, managed here on its behalf — the 0032 Rate cards pattern. The limits line is
 * the adopted rate card's live controls (TMMPDM-0089, tmm-api 0154); with no agreement an info alert links to the
 * Agreement tab and New commission plan is off. The table shows each plan's terms in words (a
 * formatting summary), its status and Non-compliant from the server's own fields; a row expands to
 * its phases and dates. Actions by status: DRAFT Edit… / Activate (confirmed); ACTIVE Close
 * (confirmed) / Copy; CLOSED Copy; a non-compliant plan's Copy is Fix this plan (the copy opens on
 * its editor page with a banner). New commission plan is an empty POST that opens the editor.
 * TMMPDM-0042 / 0055: each row opens Assigned to for that plan (filtered on commissionPlan.id).
 * Every write re-reads the list; a quiet success is a toast naming the next step, anything to act
 * on stays a banner.
 */
import { Fragment, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Collapse,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { positiveInt } from '@/hooks/useUrlState';
import { useReturnTo } from '@/hooks/useReturnTo';
import {
  ConfirmDialog,
  EditDetailsDialog,
  EmptyState,
  ErrorState,
  RowActionsMenu,
  fieldEditable,
  hasEditableFields,
  useNotify,
  type EditDetailField,
  type RowActionItem,
} from '@/components/page';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { HelpPopover } from '@/components/ui';
import { adminApi } from '@/api/tmm/admin';
import { ApiError, commissionPlanNameTaken, errorCopy, isValidationFailed } from '@/api/errors';
import type {
  CommissionPlanView,
  CommissionPlanWriteRequest,
  ResellerAffiliateRow,
  ResellerAgreementList,
} from '@/api/tmm/types';
import { formatDate, formatPercentBp } from '@/format';
import { NoncompliantChip, ResellerCardStatusChip } from './ResellerCardChips';
import { useAdoptedCardLimits } from './useAdoptedCardLimits';
import { CommissionPlanDetail } from './CommissionPlanDetail';
import {
  AFTER_END_FALLBACK,
  APPLIES_TO_FALLBACK,
  MODE_FALLBACK,
  commissionPlanEditPath,
  economicsSummary,
  termsSummary,
  type FixedFromState,
} from './commissionPlanFormat';

/** The expand button plus the eight columns. */
const COLUMNS = 9;

/** A banner the user must act on. */
interface ActionProblem {
  message: string;
  /** 409 `COMMISSION_PLAN_IMMUTABLE`: Reload re-reads the list. */
  reload?: boolean;
}

interface ResellerCommissionPlansPanelProps {
  resellerId: number;
}

export function ResellerCommissionPlansPanel({ resellerId }: ResellerCommissionPlansPanelProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawPlan = searchParams.get('plan');
  const planFromUrl = rawPlan === null ? undefined : positiveInt(rawPlan);
  const { linkState } = useReturnTo();
  const notify = useNotify();
  const [plans, setPlans] = useState<CommissionPlanView[] | null>(null);
  const [loadFailure, setLoadFailure] = useState<unknown>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [agreement, setAgreement] = useState<ResellerAgreementList | null>(null);
  const [agreementFailure, setAgreementFailure] = useState<unknown>(null);
  const [agreementToken, setAgreementToken] = useState(0);
  // A 409 AGREEMENT_REQUIRED says there is none, whatever the last agreement read said.
  const [agreementRequired, setAgreementRequired] = useState(false);
  const adopted = useAdoptedCardLimits(resellerId);
  const [expanded, setExpanded] = useState<number | null>(planFromUrl ?? null);
  const [assignedFocusPlan, setAssignedFocusPlan] = useState(planFromUrl);
  // A changed `?plan=` (including a retired affiliates link) opens that plan. A collapse
  // stays until the param itself changes.
  const [openedPlan, setOpenedPlan] = useState(planFromUrl);
  if (planFromUrl !== openedPlan) {
    setOpenedPlan(planFromUrl);
    if (assignedFocusPlan !== planFromUrl) setAssignedFocusPlan(undefined);
    if (planFromUrl !== undefined) setExpanded(planFromUrl);
  }
  const [affiliateRows, setAffiliateRows] = useState<ResellerAffiliateRow[] | null>(null);
  const [affiliatesFailure, setAffiliatesFailure] = useState<unknown>(null);
  const [assignedFocusToken, setAssignedFocusToken] = useState(0);
  const [affiliateToken, setAffiliateToken] = useState(0);
  const [confirm, setConfirm] = useState<{
    kind: 'activate' | 'close';
    plan: CommissionPlanView;
  } | null>(null);
  const [detailsId, setDetailsId] = useState<number | null>(null);
  const [detailsToken, setDetailsToken] = useState(0);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const [problem, setProblem] = useState<ActionProblem | null>(null);
  // A refused Activate or Close is said inside its confirm, which stays open (TMM-B4).
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const plansGeneration = useRef(0);

  useEffect(() => {
    const id = ++plansGeneration.current;
    let cancelled = false;
    adminApi
      .listCommissionPlans(resellerId)
      .then((list) => {
        if (cancelled || plansGeneration.current !== id) return;
        setPlans(list.commissionPlans);
        setLoadFailure(null);
      })
      .catch((error: unknown) => {
        if (cancelled || plansGeneration.current !== id) return;
        setPlans(null);
        setLoadFailure(error);
      });
    return () => {
      cancelled = true;
    };
  }, [resellerId, reloadToken]);

  // Once per visit of this tab. Every expanded row reads the same list.
  useEffect(() => {
    let cancelled = false;
    adminApi
      .listResellerAffiliates(resellerId)
      .then((list) => {
        if (cancelled) return;
        setAffiliateRows(list.affiliates);
        setAffiliatesFailure(null);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setAffiliateRows(null);
        setAffiliatesFailure(error);
      });
    return () => {
      cancelled = true;
    };
  }, [resellerId, affiliateToken]);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .getResellerAgreement(resellerId)
      .then((list) => {
        if (cancelled) return;
        setAgreement(list);
        setAgreementFailure(null);
        setAgreementRequired(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setAgreement(null);
        setAgreementFailure(error);
      });
    return () => {
      cancelled = true;
    };
  }, [resellerId, agreementToken]);

  const reload = () => setReloadToken((n) => n + 1);
  const refreshDetails = async (resetForm = true) => {
    const id = ++plansGeneration.current;
    const list = await adminApi.listCommissionPlans(resellerId);
    if (plansGeneration.current !== id) return;
    setPlans(list.commissionPlans);
    if (resetForm) setDetailsToken((n) => n + 1);
  };
  const reloadAgreement = () => setAgreementToken((n) => n + 1);

  const problemOf = (err: unknown, fallback: string, kind?: 'activate'): ActionProblem | null => {
    const code = err instanceof ApiError ? String(err.code) : '';
    if (code === 'AGREEMENT_REQUIRED') {
      // The agreement alert says it, with the way forward.
      setAgreementRequired(true);
      reloadAgreement();
      return null;
    }
    const taken = commissionPlanNameTaken(err);
    if (taken !== null) {
      // Create and copy send no name, so only the server's `details.name` can name the clash.
      return {
        message: taken
          ? t(
              'commissionPlans.errors.nameTaken',
              'Another of this reseller\'s plans is called "{{name}}".',
              { name: taken },
            )
          : fallback,
      };
    }
    if (code === 'COMMISSION_PLAN_IMMUTABLE') {
      return {
        message: t(
          'commissionPlans.errors.immutable',
          'This plan is no longer a draft. Copy it to change it.',
        ),
        reload: true,
      };
    }
    if (kind === 'activate' && isValidationFailed(err)) {
      return {
        message: t(
          'commissionPlans.errors.overLimit',
          'This plan is over a limit. Edit it to fix it.',
        ),
      };
    }
    return { message: errorCopy(err, t, fallback) };
  };

  /** One write at a time (ACT-008); every write re-reads the list, success or not. */
  const run = async (
    write: () => Promise<CommissionPlanView>,
    onDone: (plan: CommissionPlanView) => void,
    fallback: string,
    kind?: 'activate',
  ) => {
    if (inFlight.current) return;
    inFlight.current = true;
    const fromConfirm = confirm !== null;
    setBusy(true);
    setProblem(null);
    setConfirmError(null);
    try {
      onDone(await write());
      setConfirm(null);
    } catch (err) {
      const refused = problemOf(err, fallback, kind);
      if (fromConfirm && refused !== null) {
        setConfirmError(refused.message);
      } else {
        setProblem(refused);
        setConfirm(null);
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
      reload();
    }
  };

  const closeConfirm = () => {
    setConfirm(null);
    setConfirmError(null);
  };

  const edit = (plan: CommissionPlanView) =>
    void navigate(commissionPlanEditPath(resellerId, plan.id), { state: linkState });

  const newPlan = () =>
    void run(
      () => adminApi.createCommissionPlan(resellerId),
      (draft) => {
        edit(draft);
        notify.success(
          t(
            'commissionPlans.toast.created',
            '{{name}} is a new draft. Edit it, then activate it.',
            {
              name: draft.name,
            },
          ),
        );
      },
      t('commissionPlans.errors.create', "Couldn't create the commission plan."),
    );

  const copy = (plan: CommissionPlanView) =>
    void run(
      () => adminApi.copyCommissionPlan(resellerId, plan.id),
      (draft) =>
        notify.success(
          t('commissionPlans.toast.copied', '{{name}} is a new draft. Edit it, then activate it.', {
            name: draft.name,
          }),
        ),
      t('commissionPlans.errors.copy', "Couldn't copy the commission plan."),
    );

  /** Fix this plan: copy it, then open the copy's editor with a banner naming the original. */
  const fix = (plan: CommissionPlanView) =>
    void run(
      () => adminApi.copyCommissionPlan(resellerId, plan.id),
      (draft) => {
        const state: FixedFromState = { ...linkState, fixedFrom: plan.name };
        void navigate(commissionPlanEditPath(resellerId, draft.id), { state });
      },
      t('commissionPlans.errors.copy', "Couldn't copy the commission plan."),
    );

  const activate = (plan: CommissionPlanView) =>
    void run(
      () => adminApi.activateCommissionPlan(resellerId, plan.id),
      () =>
        notify.success(
          t('commissionPlans.toast.activated', 'Plan activated. Affiliates can now be given it.'),
        ),
      t('commissionPlans.errors.activate', "Couldn't activate the commission plan."),
      'activate',
    );

  const close = (plan: CommissionPlanView) =>
    void run(
      () => adminApi.closeCommissionPlan(resellerId, plan.id),
      (closed) =>
        notify.success(
          t('commissionPlans.toast.closed', '{{name}} is closed. Affiliates on it keep it.', {
            name: closed.name,
          }),
        ),
      t('commissionPlans.errors.close', "Couldn't close the commission plan."),
    );

  if (loadFailure !== null && plans === null) {
    return (
      <ErrorState
        error={loadFailure}
        message={t('commissionPlans.loadError', "Couldn't load this reseller's commission plans.")}
        onRetry={reload}
      />
    );
  }

  if (plans === null) return <LoadingSpinner />;

  const detailsPlan = plans.find((plan) => plan.id === detailsId) ?? null;
  const heading = t('commissionPlans.title', 'Commission plans');
  const current = agreement?.current ?? null;
  const noAgreement = agreementRequired || (agreement !== null && current === null);
  const card = adopted.limits;

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap' }}
        useFlexGap
      >
        <Stack direction="row" spacing={0.25} sx={{ alignItems: 'center' }}>
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
            {heading}
          </Typography>
          <HelpPopover
            label={heading}
            short={t(
              'commissionPlans.help.short',
              'Resellers design their own affiliate commission plans; there are no platform plans.',
            )}
            body={t(
              'commissionPlans.help.body',
              'An affiliate keeps its assigned plan, and each referral keeps the plan it was made under, for life. Changes here are made on the reseller’s behalf and audited as you.',
            )}
          />
        </Stack>
        <Button variant="contained" onClick={newPlan} disabled={busy || noAgreement}>
          {t('commissionPlans.actions.new', 'New commission plan')}
        </Button>
      </Stack>

      {card && !noAgreement ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t(
            'commissionPlans.limits',
            'Limits from {{card}}: recurring up to {{pct}}, cool-off up to {{days}} days.',
            {
              card: card.cardName,
              pct: formatPercentBp(card.recurringMaxBp, locale),
              days: card.coolOffMaxDays,
            },
          )}
        </Typography>
      ) : null}
      {(agreementFailure !== null || adopted.status === 'failed') && !noAgreement ? (
        <Box sx={{ mb: 2 }}>
          <ErrorState
            error={agreementFailure ?? adopted.error}
            message={t(
              'commissionPlans.limitsError',
              "Couldn't load the limits from the adopted rate card.",
            )}
            onRetry={() => {
              if (agreementFailure !== null) reloadAgreement();
              if (adopted.status === 'failed') adopted.retry();
            }}
          />
        </Box>
      ) : null}
      {noAgreement ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t(
            'commissionPlans.noAgreement',
            'This reseller needs an agreement before it can have commission plans.',
          )}{' '}
          <Link component={RouterLink} to="?tab=agreement" underline="always">
            {t('commissionPlans.openAgreement', 'Open the Agreement tab')}
          </Link>
        </Alert>
      ) : null}

      {problem !== null ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setProblem(null)}
          action={
            problem.reload ? (
              <Button color="inherit" size="small" onClick={reload}>
                {t('common.reload', 'Reload')}
              </Button>
            ) : undefined
          }
        >
          {problem.message}
        </Alert>
      ) : null}

      {plans.length === 0 ? (
        <Paper variant="outlined">
          <EmptyState title={t('commissionPlans.empty', 'No commission plans yet.')} />
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table
            size="small"
            aria-label={t('commissionPlans.tableAria', "The reseller's commission plans")}
          >
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>{t('commissionPlans.col.name', 'Name')}</TableCell>
                <TableCell>{t('commissionPlans.col.mode', 'Mode')}</TableCell>
                <TableCell>{t('commissionPlans.col.terms', 'Terms')}</TableCell>
                <TableCell>{t('commissionPlans.col.appliesTo', 'Applies to')}</TableCell>
                <TableCell>{t('commissionPlans.col.coolOff', 'Cool-off')}</TableCell>
                <TableCell>{t('commissionPlans.col.afterEnd', 'After end')}</TableCell>
                <TableCell>{t('commissionPlans.col.status', 'Status')}</TableCell>
                <TableCell>{t('commissionPlans.col.actions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map((plan) => {
                const open = expanded === plan.id;
                return (
                  <Fragment key={plan.id}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton
                          size="small"
                          aria-expanded={open}
                          aria-label={
                            open
                              ? t('commissionPlans.collapse', 'Collapse {{name}}', {
                                  name: plan.name,
                                })
                              : t('commissionPlans.expand', 'Expand {{name}}', { name: plan.name })
                          }
                          onClick={() => {
                            setAssignedFocusPlan(undefined);
                            setExpanded(open ? null : plan.id);
                          }}
                        >
                          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{plan.name}</TableCell>
                      <TableCell>
                        {t(`commissionPlans.mode.${plan.mode}`, MODE_FALLBACK[plan.mode])}
                      </TableCell>
                      <TableCell sx={{ minWidth: 140 }}>
                        {termsSummary(plan.phases, t, locale)}
                        {economicsSummary(plan, t, locale).map((line) => (
                          <Typography key={line} variant="body2" color="text.secondary">
                            {line}
                          </Typography>
                        ))}
                      </TableCell>
                      <TableCell>
                        {t(
                          `commissionPlans.appliesTo.${plan.appliesTo}`,
                          APPLIES_TO_FALLBACK[plan.appliesTo],
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {t('common.days', '{{count}} days', { count: plan.coolOffDays })}
                      </TableCell>
                      <TableCell>
                        {t(
                          `commissionPlans.afterEnd.${plan.afterEnd}`,
                          AFTER_END_FALLBACK[plan.afterEnd],
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                          <ResellerCardStatusChip status={plan.status} />
                          <NoncompliantChip
                            card={plan}
                            explanation={t(
                              'commissionPlans.noncompliant.explanation',
                              "Over a limit since {{date}}. It can't be assigned to more affiliates; affiliates on it keep it.",
                              { date: formatDate(plan.noncompliantSince, locale) },
                            )}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={1}
                          useFlexGap
                          sx={{ flexWrap: 'wrap', alignItems: 'center' }}
                        >
                          {plan.status === 'DRAFT' ? (
                            <Button size="small" onClick={() => edit(plan)} disabled={busy}>
                              {t('commissionPlans.actions.edit', 'Edit…')}
                            </Button>
                          ) : plan.noncompliant ? (
                            <Button size="small" onClick={() => fix(plan)} disabled={busy}>
                              {t('commissionPlans.actions.fix', 'Fix this plan')}
                            </Button>
                          ) : (
                            <Button size="small" onClick={() => copy(plan)} disabled={busy}>
                              {t('rateCards.actions.copy', 'Copy')}
                            </Button>
                          )}
                          <RowActionsMenu
                            name={plan.name}
                            items={commissionPlanMenu(plan, t, {
                              onEditDetails: () => setDetailsId(plan.id),
                              onActivate: () => setConfirm({ kind: 'activate', plan }),
                              onClose: () => setConfirm({ kind: 'close', plan }),
                            })}
                          />
                          <Button
                            size="small"
                            onClick={() => {
                              setExpanded(plan.id);
                              setAssignedFocusPlan(plan.id);
                              setAssignedFocusToken((n) => n + 1);
                              setSearchParams(
                                (current) => {
                                  const next = new URLSearchParams(current);
                                  next.set('plan', String(plan.id));
                                  return next;
                                },
                                { replace: true },
                              );
                            }}
                          >
                            {t('commissionPlans.actions.onThisPlan', 'Affiliates on this plan')}
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={COLUMNS}
                        sx={{ py: 0, borderBottom: open ? undefined : 'none' }}
                      >
                        <Collapse in={open} timeout="auto" unmountOnExit>
                          <CommissionPlanDetail
                            plan={plan}
                            resellerId={resellerId}
                            affiliates={affiliateRows}
                            affiliatesFailure={affiliatesFailure}
                            focusAssigned={open && assignedFocusPlan === plan.id}
                            focusToken={assignedFocusToken}
                            onRetryAffiliates={() => {
                              setAffiliatesFailure(null);
                              setAffiliateRows(null);
                              setAffiliateToken((n) => n + 1);
                            }}
                          />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ConfirmDialog
        open={confirm?.kind === 'activate'}
        title={t('commissionPlans.activate.title', 'Activate {{name}}?', {
          name: confirm?.plan.name ?? '',
        })}
        consequence={t(
          'commissionPlans.activate.confirm',
          "Activate {{name}}? An active plan can't be edited; copy it to change it.",
          { name: confirm?.plan.name ?? '' },
        )}
        confirmLabel={t('rateCards.actions.activate', 'Activate')}
        busy={busy}
        error={confirmError}
        onCancel={closeConfirm}
        onConfirm={() => {
          if (confirm) activate(confirm.plan);
        }}
      />
      <ConfirmDialog
        open={confirm?.kind === 'close'}
        title={t('commissionPlans.close.title', 'Close {{name}}?', {
          name: confirm?.plan.name ?? '',
        })}
        consequence={t(
          'commissionPlans.close.confirm',
          'Close {{name}}? No more affiliates can be given it; affiliates on it keep it.',
          { name: confirm?.plan.name ?? '' },
        )}
        confirmLabel={t('rateCards.actions.close', 'Close')}
        destructive
        busy={busy}
        error={confirmError}
        onCancel={closeConfirm}
        onConfirm={() => {
          if (confirm) close(confirm.plan);
        }}
      />
      <EditDetailsDialog
        open={detailsPlan !== null}
        entityLabel={t('components.editDetails.entity.commissionPlan', 'commission plan')}
        fields={detailsPlan ? commissionPlanDetailFields(detailsPlan, t) : []}
        version={detailsPlan ? detailsPlan.version : null}
        onClose={() => setDetailsId(null)}
        onReload={refreshDetails}
        onRefreshAfterSave={() => refreshDetails(false)}
        reloadToken={detailsToken}
        onSubmit={async (changed, version) => {
          if (!detailsPlan) return;
          const body: CommissionPlanWriteRequest = {};
          if (changed.name !== undefined) body.name = changed.name.trim();
          if (version !== null) body.version = version;
          const saved = await adminApi.patchCommissionPlan(resellerId, detailsPlan.id, body);
          ++plansGeneration.current;
          setPlans(
            (current) => current?.map((plan) => (plan.id === saved.id ? saved : plan)) ?? [saved],
          );
        }}
      />
    </Box>
  );
}

function commissionPlanMenu(
  plan: CommissionPlanView,
  t: (key: string, fallback?: string) => string,
  actions: { onEditDetails: () => void; onActivate: () => void; onClose: () => void },
): RowActionItem[] {
  const items: RowActionItem[] = [];
  if (hasEditableFields(plan.editableFields)) {
    items.push({
      key: 'details',
      label: t('components.editDetails.menu', 'Edit details…'),
      onSelect: actions.onEditDetails,
    });
  }
  if (plan.status === 'DRAFT') {
    items.push({
      key: 'activate',
      label: t('rateCards.actions.activate', 'Activate'),
      onSelect: actions.onActivate,
    });
  }
  if (plan.status === 'ACTIVE') {
    items.push({
      key: 'close',
      label: t('rateCards.actions.close', 'Close'),
      destructive: true,
      onSelect: actions.onClose,
    });
  }
  return items;
}

function commissionPlanDetailFields(
  plan: CommissionPlanView,
  t: (key: string, fallback?: string) => string,
): EditDetailField[] {
  if (!fieldEditable(plan.editableFields, 'name')) return [];
  return [
    {
      key: 'name',
      label: t('commissionPlans.col.name', 'Name'),
      value: plan.name,
      required: true,
      maxLength: 80,
    },
  ];
}
